import React, { createContext, useContext, useState, useCallback } from 'react';
import { ProgressBar } from '../components/ui/progress-bar';
import { X } from 'lucide-react';

const ProgressContext = createContext(null);

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);

  // Start a new progress task
  const startProgress = useCallback((taskId, label) => {
    setTasks(prev => [...prev, { 
      id: taskId, 
      label, 
      progress: 0, 
      status: 'running',
      startTime: Date.now()
    }]);
    return taskId;
  }, []);

  // Update progress for a task
  const updateProgress = useCallback((taskId, progress, newLabel) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, progress, label: newLabel || task.label } 
        : task
    ));
  }, []);

  // Complete a task
  const completeProgress = useCallback((taskId) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, progress: 100, status: 'complete' } 
        : task
    ));
    // Auto-remove after 2 seconds
    const timerId = setTimeout(() => {
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }, 2000);
    return () => clearTimeout(timerId);
  }, []);

  // Error a task
  const errorProgress = useCallback((taskId, error) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, status: 'error', error } 
        : task
    ));
    // Auto-remove after 5 seconds
    const timerId = setTimeout(() => {
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }, 5000);
    return () => clearTimeout(timerId);
  }, []);

  // Cancel/remove a task
  const cancelProgress = useCallback((taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  // Calculate estimated time
  const getEstimatedTime = (task) => {
    if (task.progress <= 0) return null;
    const elapsed = Date.now() - task.startTime;
    const totalEstimate = (elapsed / task.progress) * 100;
    const remaining = totalEstimate - elapsed;
    
    if (remaining < 1000) return 'Almost done...';
    if (remaining < 60000) return `~${Math.ceil(remaining / 1000)}s remaining`;
    return `~${Math.ceil(remaining / 60000)}m remaining`;
  };

  return (
    <ProgressContext.Provider value={{ 
      startProgress, 
      updateProgress, 
      completeProgress, 
      errorProgress,
      cancelProgress 
    }}>
      {children}
      
      {/* Global Progress Bar Overlay - Top Center */}
      {tasks.length > 0 && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] space-y-2 min-w-[300px] max-w-[500px]">
          {tasks.map(task => (
            <div 
              key={task.id} 
              className={`
                bg-zinc-900/95 backdrop-blur-md border rounded-lg p-3 shadow-xl
                ${task.status === 'error' ? 'border-red-500/50' : 'border-zinc-700'}
                ${task.status === 'complete' ? 'border-green-500/50' : ''}
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-zinc-200 font-medium truncate flex-1">
                  {task.label}
                </span>
                <button 
                  onClick={() => cancelProgress(task.id)}
                  className="ml-2 text-zinc-500 hover:text-zinc-300 p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              
              {task.status === 'error' ? (
                <div className="text-xs text-red-400">{task.error || 'An error occurred'}</div>
              ) : (
                <>
                  <ProgressBar 
                    progress={task.progress} 
                    showPercentage={true}
                  />
                  {task.status === 'running' && task.progress > 0 && (
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {getEstimatedTime(task)}
                    </div>
                  )}
                  {task.status === 'complete' && (
                    <div className="text-[10px] text-green-400 mt-1">
                      ✓ Complete
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </ProgressContext.Provider>
  );
};

export default ProgressProvider;
